export interface ConditionalField {
  id: string;
  required: boolean;
  conditions?: Array<{
    sourceFieldId: string;
    operator: string;
    values: unknown[];
  }> | null;
  conditionCombinator?: "all" | "any" | null;
  requiredWhenVisible?: boolean | null;
}

/** True only when the source field has a real user-supplied value. */
function hasAnswer(answer: unknown): boolean {
  if (answer === null || answer === undefined) return false;
  if (typeof answer === "boolean") return true;
  if (typeof answer === "string") return answer.length > 0;
  if (Array.isArray(answer)) return answer.length > 0;
  return true;
}

function matchValue(answer: unknown, condVal: unknown): boolean {
  if (typeof answer === "boolean" || typeof condVal === "boolean") {
    return answer === condVal;
  }
  return String(answer ?? "") === String(condVal ?? "");
}

export function computeVisibleFieldIds(
  fields: ConditionalField[],
  answers: Record<string, unknown>,
): Set<string> {
  const visible = new Set<string>();
  for (const f of fields) {
    if (!f.conditions || f.conditions.length === 0) {
      visible.add(f.id);
      continue;
    }
    const results = f.conditions.map((c) => {
      if (!visible.has(c.sourceFieldId)) return false;
      const answer = answers[c.sourceFieldId];
      // An unanswered source never satisfies any condition (guards all operators).
      if (!hasAnswer(answer)) return false;
      if (c.operator === "equals") {
        if (Array.isArray(answer)) return answer.some((v) => matchValue(v, c.values[0]));
        return matchValue(answer, c.values[0]);
      }
      if (c.operator === "not-equals") {
        if (Array.isArray(answer)) return !answer.some((v) => matchValue(v, c.values[0]));
        return !matchValue(answer, c.values[0]);
      }
      if (c.operator === "is-any-of") {
        if (Array.isArray(answer)) return answer.some((av) => c.values.some((cv) => matchValue(av, cv)));
        return c.values.some((cv) => matchValue(answer, cv));
      }
      return false;
    });
    const combinator = f.conditionCombinator ?? "all";
    const shown = results.length > 0 && (combinator === "any" ? results.some(Boolean) : results.every(Boolean));
    if (shown) visible.add(f.id);
  }
  return visible;
}

export function isFieldEffectivelyRequired(
  field: ConditionalField,
  visibleIds: Set<string>,
): boolean {
  if (!visibleIds.has(field.id)) return false;
  const hasConditions = (field.conditions?.length ?? 0) > 0;
  return field.required || (field.requiredWhenVisible === true && hasConditions);
}

/** Minimal shape for stale-condition detection (server and client field types both satisfy it). */
export interface FieldWithConditions {
  id: string;
  label?: string | null;
  conditions?: Array<{ sourceFieldId: string }> | null;
}

export interface StaleConditionField<T extends FieldWithConditions> {
  field: T;
  staleSourceIds: string[];
}

/**
 * A condition is "stale" when its `sourceFieldId` does not refer to a field that
 * appears *before* the field that owns the condition. This covers both deleted
 * source fields and fields reordered so they no longer precede the dependent
 * field — in both cases the visibility engine can never satisfy the condition,
 * so the branching logic is silently broken. Returns one entry per field that
 * has at least one stale condition.
 */
export function findStaleConditionFields<T extends FieldWithConditions>(
  fields: T[],
): StaleConditionField<T>[] {
  const result: StaleConditionField<T>[] = [];
  fields.forEach((field, idx) => {
    const conditions = field.conditions ?? [];
    if (conditions.length === 0) return;
    const priorIds = new Set(fields.slice(0, idx).map((f) => f.id));
    const staleSourceIds = conditions
      .map((c) => c.sourceFieldId)
      .filter((sid) => !priorIds.has(sid));
    if (staleSourceIds.length > 0) result.push({ field, staleSourceIds });
  });
  return result;
}

/**
 * Forward pass that mirrors `computeVisibleFieldIds`'s core rule ("a condition
 * can only be satisfied by a source field that appears *before* it and is
 * itself shown"), but evaluated over *every possible* applicant answer instead
 * of one concrete answer set. A field is "reachable" when some answer set could
 * make it visible:
 *  - no conditions → always reachable;
 *  - with conditions → each condition is satisfiable only if its source field
 *    precedes this field AND is itself reachable (so an applicant could in
 *    principle answer it to satisfy the rule). The `all`/`any` combinator then
 *    decides whether the field as a whole can be shown.
 *
 * Condition *values* are intentionally not evaluated (the applicant controls
 * them), so this over-approximates reachability: it will never flag a field
 * that is in fact reachable, keeping false positives off the host's screen.
 */
export function computeReachableFieldIds(fields: ConditionalField[]): Set<string> {
  const reachable = new Set<string>();
  fields.forEach((field, idx) => {
    const conditions = field.conditions ?? [];
    if (!conditions || conditions.length === 0) {
      reachable.add(field.id);
      return;
    }
    const priorIds = new Set(fields.slice(0, idx).map((f) => f.id));
    const satisfiable = conditions.map(
      (c) => priorIds.has(c.sourceFieldId) && reachable.has(c.sourceFieldId),
    );
    const combinator = field.conditionCombinator ?? "all";
    const ok = combinator === "any" ? satisfiable.some(Boolean) : satisfiable.every(Boolean);
    if (ok) reachable.add(field.id);
  });
  return reachable;
}

/** A page-bearing field: the conditional shape plus its 1-based page number. */
export interface PagedConditionalField extends ConditionalField {
  page?: number | null;
}

/**
 * Returns the (sorted) page numbers that can never be shown to an applicant
 * because every field on the page is unreachable — its show/hide rules can
 * never be satisfied (the source was deleted, reordered to appear later, or is
 * itself on an unreachable branch). Pages with no fields are not reported (the
 * editor renders an empty-page hint for those instead). Uses
 * `computeReachableFieldIds` so the rule stays identical to the live form's
 * visibility engine rather than a separate ad-hoc check.
 */
export function findUnreachablePages(fields: PagedConditionalField[]): number[] {
  const reachable = computeReachableFieldIds(fields);
  const byPage = new Map<number, PagedConditionalField[]>();
  for (const f of fields) {
    const page = f.page ?? 1;
    const arr = byPage.get(page) ?? [];
    arr.push(f);
    byPage.set(page, arr);
  }
  const unreachable: number[] = [];
  for (const [page, pageFields] of byPage) {
    if (pageFields.length === 0) continue;
    if (!pageFields.some((f) => reachable.has(f.id))) unreachable.push(page);
  }
  return unreachable.sort((a, b) => a - b);
}
